import { Check } from "lucide-react";
import { useId } from "react";

export type CheckboxVariant = "default" | "brand" | "signature";

const VARIANT_CHECKED_CLASSES: Record<CheckboxVariant, string> = {
  default: "bg-content-primary",
  brand: "bg-brand-primary",
  signature: "bg-brand-gradient",
};

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;

  description?: string;

  variant?: CheckboxVariant;
  disabled?: boolean;
  id?: string;

  "aria-label"?: string;
}

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  variant = "default",
  disabled = false,
  id,
  "aria-label": ariaLabel,
}: Readonly<CheckboxProps>) {
  const autoId = useId();
  const resolvedId = id ?? autoId;

  const box = (
    <button
      id={resolvedId}
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        "grid size-4 shrink-0 place-items-center rounded transition-colors",
        "btn-focus-ring",
        checked ? VARIANT_CHECKED_CLASSES[variant] : "border border-border-subtle bg-surface-base",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      ].join(" ")}
    >
      {checked && <Check size={12} className="text-content-inverse" strokeWidth={3} />}
    </button>
  );

  if (!label) return box;

  return (
    <label
      htmlFor={resolvedId}
      className={["flex items-start gap-2", disabled ? "cursor-not-allowed" : "cursor-pointer"].join(" ")}
    >
      {}
      <span className="mt-0.5">{box}</span>
      <span>
        <span className="block text-sm font-medium text-content-secondary">{label}</span>
        {description && <span className="block text-xs text-content-tertiary">{description}</span>}
      </span>
    </label>
  );
}
